import { useState } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import type { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import { QuoteItemRow } from './components/rows/QuoteItemRow'
import { useOrderTableData } from './hooks'
import { Table, TableBody } from 'src/shared/components/ui/table'
import { OrderEmptyState } from './components/layout/OrderEmptyState'
import { OrderTableAdminActions } from './components/layout/OrderTableAdminActions'
import { OrderTableHeader } from './components/layout/OrderTableHeader'
import { OrderTableFooter } from './components/layout/OrderTableFooter'
import { QuoteItemRowSpacer } from './components/layout/QuoteItemRowSpacer'
import { QuoteAlternativeLabelRow } from './components/layout/QuoteAlternativeLabelRow'
import { activeQuoteLineIds } from 'src/shared/utils/quoteAlternatives'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { Card } from '../ui'
import { cn } from '../ui/utils'
import {
  effectiveQuoteStatus,
  getQuoteStatusPhaseIndex,
} from 'src/shared/consts/quoteStatus'

export const OrderTable = ({
  quote_id,
  quoteItems: quoteItemsProp,
  quotePublicId,
  quoteStatus,
  isReadOnly = false,
  chatView = false,
  paperView = false,
}: {
  quote_id?: number
  quoteItems?: QuoteItem_[]
  quotePublicId?: string
  quoteStatus?: string | null
  isReadOnly?: boolean
  chatView?: boolean
  paperView?: boolean
}) => {
  const { t } = useLocalization()
  const isAdmin = useIsAdmin()
  const isQuoteCommitted =
    getQuoteStatusPhaseIndex(effectiveQuoteStatus(quoteStatus)) >= 1
  const effectiveReadOnly =
    isReadOnly || (!isAdmin && isQuoteCommitted)
  const [isEditMode, setIsEditMode] = useState(false)

  const { quoteItems, total_price, footerSubtotals, createQuoteItem } =
    useOrderTableData({
      quote_id,
      quoteItems: quoteItemsProp,
    })

  if (chatView && (!quoteItems.data || quoteItems.data.length === 0)) {
    return <OrderEmptyState variant="chat" />
  }

  if (!quoteItems.data || quoteItems.data.length === 0) {
    return <OrderEmptyState variant="catalog" />
  }

  // Proposal alternatives: read-only views (customer, invoice) list only the
  // lines that count; staff who can edit still see every alternative, marked.
  const activeLineIds = activeQuoteLineIds(quoteItems.data)
  const showInactiveAlternatives = isAdmin && !effectiveReadOnly
  const visibleItems = showInactiveAlternatives
    ? quoteItems.data
    : quoteItems.data.filter((item) => activeLineIds.has(item.id))

  const hasDiscount = quoteItems.data?.some((item) => (item.discount || 0) > 0)
  // Keep column layout identical to view; discount is edited in the receipt stack.
  const showDiscountColumn = hasDiscount && !isEditMode

  const handleAddRow = () => {
    if (quote_id) {
      createQuoteItem.mutate({
        quote_id,
        type: 'custom',
        description: t('Custom item', 'Anpassad rad'),
        quantity: 1,
        unit_price: 0,
        discount: 0,
      })
    }
  }

  return (
    <Card
      variant="plain"
      className={cn(
        paperView
          ? 'mx-auto w-full max-w-[800px] shrink-0 border border-gray-200/80 px-8 py-8 shadow-md'
          : 'p-0 shadow-none hover:!shadow-none',
        !chatView && !paperView && 'border-none'
      )}
    >
      <Column id="products" className="min-h-[300px] gap-4 max-sm:min-h-0">
        <Table className="table-fixed border-separate border-spacing-0 max-sm:block max-sm:w-full max-sm:[&_tbody]:block max-sm:[&_tbody_tr]:flex max-sm:[&_tbody_tr]:flex-col max-sm:[&_td]:block max-sm:[&_td]:w-full">
          <colgroup className="max-sm:hidden">
            <col className={showDiscountColumn ? 'w-[42%]' : 'w-[45%]'} />
            {showDiscountColumn && <col className="w-[10%]" />}
            <col className={showDiscountColumn ? 'w-[48%]' : 'w-[55%]'} />
          </colgroup>

          <OrderTableHeader showDiscountColumn={showDiscountColumn} />

          <TableBody className="pt-1 [&_tr:last-child]:border-0">
            {visibleItems
              .slice()
              ?.sort((a, b) => {
                const typeOrder = {
                  product: 0,
                  shipping: 1,
                  custom: 2,
                } as const

                const typeDiff =
                  (typeOrder[a.type as keyof typeof typeOrder] ?? 999) -
                  (typeOrder[b.type as keyof typeof typeOrder] ?? 999)
                if (typeDiff !== 0) return typeDiff

                return (b.created_at ?? '').localeCompare(a.created_at ?? '')
              })
              .flatMap((quote_item, index, items) => {
                const row = (
                  <QuoteItemRow
                    key={quote_item.id}
                    quote_item={quote_item}
                    isEditMode={isEditMode}
                    showDiscountColumn={showDiscountColumn}
                    isReadOnly={effectiveReadOnly}
                  />
                )
                const rows = activeLineIds.has(quote_item.id)
                  ? [row]
                  : [
                      <QuoteAlternativeLabelRow
                        key={`alternative-${quote_item.id}`}
                        slot={quote_item.slot ?? ''}
                        colSpan={showDiscountColumn ? 3 : 2}
                      />,
                      row,
                    ]

                if (index === items.length - 1) return rows

                return [
                  ...rows,
                  <QuoteItemRowSpacer
                    key={`spacer-${quote_item.id}`}
                    colSpan={showDiscountColumn ? 3 : 2}
                  />,
                ]
              })}
          </TableBody>
        </Table>

        <Row className="mt-auto items-end justify-between max-sm:w-full max-sm:flex-col max-sm:items-stretch max-sm:gap-4">
          {isAdmin && !effectiveReadOnly && (
            <OrderTableAdminActions
              isEditMode={isEditMode}
              onToggleEditMode={() => setIsEditMode((v) => !v)}
              onAddRow={handleAddRow}
              isAddRowPending={createQuoteItem.isPending}
            />
          )}

          <OrderTableFooter
            footerSubtotals={footerSubtotals}
            totalPrice={total_price}
            quotePublicId={quotePublicId}
            chatView={chatView}
          />
        </Row>
      </Column>
    </Card>
  )
}
